<template>
    <div class="faster-payoff">
        <div class="container-width">
            <h2 class="section-title">
                Strategies to reach your payoff day faster
            </h2>
            <h4>These small tweaks to the way you pay can add up to big savings. Build your strategy below and see how much time and money you can save.</h4>

            <div class="left-block">
                <ul class="filter-block">
                    <li>
                        <label class="title">Pay more frequently</label> <a
                            href="#"
                            class="what-this-link">What’s this?</a>
                        <ul class="radio-section">
                            <li>
                                <input
                                    id="monthly"
                                    :value="paymentFrequencyVal"
                                    type="radio"
                                    name="pay-type"
                                    @change="getPaymentFrequency('monthly')"
                                /> <label for="monthly">Monthly</label>
                            </li>
                            <li>
                                <input
                                    id="bi-weekly"
                                    :value="paymentFrequencyVal"
                                    type="radio"
                                    name="pay-type"
                                    @change="getPaymentFrequency('bi-weekly')"
                                /> <label for="bi-weekly">Bi-weekly</label>
                            </li>
                            <li>
                                <input
                                    id="weekly"
                                    :value="paymentFrequencyVal"
                                    type="radio"
                                    name="pay-type"
                                    @change="getPaymentFrequency('weekly')"
                                /> <label for="weekly">Weekly</label>
                            </li>
                        </ul>
                    </li>
                    <li>
                        <label class="title">Overpay each payment</label> <a
                            href="#"
                            class="what-this-link">What’s this?</a>
                        <span class="muted-text">Amount to pay extra per payment?</span>
                        <div class="range-slider">
                            <div class="slider-val">
                                <div><span>$0</span></div>
                                <number-input
                                    v-model="overpaySlider.value"
                                    apply-input-group-addon-class="hidden"
                                    apply-input-group-class="selected-amt"
                                    apply-input-class="slider-input"
                                    @input="inputOverPayVal(overpaySlider.value)"
                                    @inputBlur="formatOverPayValue"
                                />
                                <div><span>$500</span></div>
                            </div>
                            <vue-slider
                                v-model="overpaySlider.value"
                                :clickable="overpaySlider.clickable"
                                :bg-style="overpaySlider.bgStyle"
                                :dot-size="overpaySlider.dotSize"
                                :height="overpaySlider.height"
                                :interval="overpaySlider.interval"
                                :max="overpaySlider.max"
                                :min="overpaySlider.min"
                                :tooltip="overpaySlider.tooltip"
                                @drag-end="getOverPayVal($event.currentValue)"
                            />
                        </div>
                    </li>
                    <li>
                        <label class="title">Cash Bombs</label> <a
                            href="#"
                            class="what-this-link">What’s this?</a>
                        <span class="muted-text">Total lump sum you will pay and how often?</span>
                        <div class="range-slider">
                            <div class="slider-val">
                                <div><span>$0</span></div>
                                <number-input
                                    v-model="cashBombSlider.value"
                                    apply-input-group-addon-class="hidden"
                                    apply-input-group-class="selected-amt"
                                    apply-input-class="slider-input"
                                    @input="inputCashBombVal(cashBombSlider.value)"
                                    @inputBlur="formatCashBombValue"
                                />
                                <div><span>$100K</span></div>
                            </div>
                            <vue-slider
                                v-model="cashBombSlider.value"
                                :bg-style="cashBombSlider.bgStyle"
                                :clickable="cashBombSlider.clickable"
                                :dot-size="cashBombSlider.dotSize"
                                :height="cashBombSlider.height"
                                :interval="cashBombSlider.interval"
                                :max="cashBombSlider.max"
                                :min="cashBombSlider.min"
                                :tooltip="cashBombSlider.tooltip"
                                @drag-end="getCashBombVal($event.currentValue)"
                            />
                        </div>
                        <ul class="radio-section">
                            <li>
                                <input
                                    id="one-time"
                                    v-model="cashBombs"
                                    type="radio"
                                    name="cash-bombs"
                                    value="one-time"
                                    @change="getCashBombFrequency()"
                                /> <label for="one-time">One Time</label>
                            </li>
                            <li>
                                <input
                                    id="yearly"
                                    v-model="cashBombs"
                                    type="radio"
                                    name="cash-bombs"
                                    value="yearly"
                                    @change="getCashBombFrequency()"
                                /> <label for="yearly">Yearly</label>
                            </li>
                            <li>
                                <input
                                    id="quarterly"
                                    v-model="cashBombs"
                                    type="radio"
                                    name="cash-bombs"
                                    value="quarterly"
                                    @change="getCashBombFrequency()"
                                /> <label for="quarterly">Quarterly</label>
                            </li>
                        </ul>
                        <div class="clear-fix"></div>
                        <div class="date-picker-block">
                            <span class="muted-text">Paid on</span>
                            <datepicker
                                v-model="currentSelectedDate"
                                format="MMMM, D YYYY"
                                lang="en"
                                :editable="false"
                                @input="updatedDate">
                                <span slot="icon-calendar"><img src="../../../assets/images/date-picker.png" /></span>
                            </datepicker>
                        </div>
                    </li>
                </ul>
                <div class="extra-pay-btn visible-xs">
                    <button class="btn btn-blue">
                        Remove Extra Payments
                    </button>
                </div>
            </div>

            <div class="right-block">
                <div class="chart-parent">
                    <vue-chart
                        v-if="showChart"
                        :data="chartData"
                        :height="chartHeight"
                        :options="options"
                        :width="chartWidth"
                        type="doughnut"
                    />
                    <div class="chart-content">
                        <h4>You Would Save</h4>
                        <label>${{ currencyFilterWithPrecision(saveInterest) }}</label>
                        <small class="light-gray-text">In total interest</small>
                        <label class="light-gray-text">&</label>
                        <label>{{ saveYears }} yrs<span v-if="saveMonths>0"> {{ saveMonths }} mo</span></label>
                        <small class="light-gray-text">Paid off sooner</small>
                    </div>
                </div>
                <h4 class="pay-amt">
                    Payment amount: <big>${{ currencyFilterWithPrecision(totalMoPayment) }}/{{ getFrequencyInString(paymentFrequency) }}</big>
                </h4>

                <ul class="chart-info">
                    <li><span class="green-bg"></span> Principal</li>
                    <li><span class="blue-bg"></span> Extra Payment</li>
                    <li><span class="red-bg"></span> Interest</li>
                    <li><span class="orange-bg"></span> HOA , Interest, PMI</li>
                </ul>
            </div>
            <div class="clear-fix"></div>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import vueSlider from 'vue-slider-component';
    import Datepicker from 'vue2-datepicker';
    import Accounting from 'accounting';
    import NumberInput from '~/components/number-input.vue';

    export default {
        components: {
            VueChart,
            vueSlider,
            Datepicker,
            NumberInput,
        },
        mixins: [Accounting],
        props: {
            totalMoPayment: {
                default: '',
            },
            paymentFrequency: {
                default: 'monthly',
            },
            allTotalInterest: {
                default: '',
            },
            allTotalExtraPayments: {
                default: '',
            },
            allTotalPrincipal: {
                default: '',
            },
            allTotalTaxesFees: {
                default: '',
            },
            saveYears: {
                default: 0,
            },
            saveMonths: {
                default: 0,
            },
            saveInterest: {
                default: 0,
            },
            currentDate: {
                default: '',
            },
            loanTermInYears: {
                default: 0,
            },
        },
        data() {
            return {
                options: {
                    cutoutPercentage: 92,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom(tooltip) {
                            let tooltipEl = document.getElementById('chartjs-tooltip-payoff');
                            if (!tooltipEl) {
                                tooltipEl = document.createElement('div');
                                tooltipEl.id = 'chartjs-tooltip-payoff';
                                tooltipEl.innerHTML = '<div></div>';
                                this._chart.canvas.parentNode.appendChild(tooltipEl);
                            }
                            if (tooltip.opacity === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            tooltip.displayColors = false;
                            tooltipEl.classList.remove('above', 'below', 'no-transform');
                            if (tooltip.yAlign) {
                                tooltipEl.classList.add(tooltip.yAlign);
                            } else {
                                tooltipEl.classList.add('no-transform');
                            }
                            function getBody(bodyItem) {
                                return bodyItem.lines;
                            }
                            // Set Text
                            if (tooltip.body) {
                                let innerHtml = '';
                                const titleLines = tooltip.title || [];
                                const bodyLines = tooltip.body.map(getBody);
                                titleLines.forEach((title) => {
                                    innerHtml += `<label>${title}</label>`;
                                });
                                bodyLines.forEach((body) => {
                                    const bodaySplit = body[0].split(':');
                                    innerHtml += `<label>${bodaySplit[0]}</label>`;
                                    innerHtml += `<h3>${bodaySplit[1]}%</h3>`;
                                });
                                const tableRoot = tooltipEl.querySelector('div');
                                tableRoot.innerHTML = innerHtml;
                            }
                            const positionY = this._chart.canvas.offsetTop;
                            const positionX = this._chart.canvas.offsetLeft;
                            tooltipEl.style.opacity = 1;
                            tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
                            tooltipEl.style.top = `${positionY - 102 + tooltip.caretY}px`;
                            tooltipEl.style.height = '100px';
                        },
                    },
                },
                chartWidth: 400,
                chartHeight: 400,

                // Setting for range slider
                overpaySlider: {
                    value: 0,
                    height: 3,
                    sliderStyle: {
                        backgroundColor: '#00aeef',
                    },
                    bgStyle: {
                        background: '#666',
                    },
                    dotSize: 16,
                    tooltip: false,
                    clickable: false,
                    min: 0,
                    max: 500,
                    interval: 5,
                },
                cashBombSlider: {
                    value: 0,
                    height: 3,
                    sliderStyle: {
                        backgroundColor: '#00aeef',
                    },
                    bgStyle: {
                        background: '#666',
                    },
                    dotSize: 16,
                    tooltip: false,
                    clickable: false,
                    min: 0,
                    max: 100000,
                    interval: 100,
                },
                currentSelectedDate: new Date(),
                currentDisplayDate: '',
                cashBombs: 'one-time',
                showChart: true,
                chartData: {},
            };
        },
        computed: {
            CurrentDate() {
                return this.currentDate;
            },
            paymentFrequencyVal() {
                return this.paymentFrequency;
            },
            chartDataPayoff() {
                this.quicklyBlinkWithChart();

                let result = {};
                let { allTotalPrincipal, allTotalExtraPayments, allTotalInterest, allTotalTaxesFees } = this;

                allTotalPrincipal = Number(allTotalPrincipal);
                allTotalExtraPayments = Number(allTotalExtraPayments);
                allTotalInterest = Number(allTotalInterest);
                allTotalTaxesFees = Number(allTotalTaxesFees);

                if (allTotalPrincipal >= 0 && allTotalExtraPayments >= 0 && allTotalInterest >= 0 && allTotalTaxesFees >= 0) {
                    const total = allTotalPrincipal + allTotalExtraPayments + allTotalInterest + allTotalTaxesFees;
                    if (total >= 0) {
                        result = {
                            labels: [
                                `Principal <b>$${allTotalPrincipal.toFixed(2)}</b>`,
                                `Extra Payment <b>$${allTotalExtraPayments.toFixed(2)}</b>`,
                                `Interest <b>$${allTotalInterest.toFixed(2)}</b>`,
                                `HOA, Interest, PMI <b>$${allTotalTaxesFees.toFixed(2)}</b>`,
                            ],
                            datasets: [
                                {
                                    data: [
                                        allTotalPrincipal !== 0 ? Math.round(Math.abs(allTotalPrincipal / total * 100)) : 0,
                                        allTotalExtraPayments !== 0 ? Math.round(Math.abs(allTotalExtraPayments / total * 100)) : 0,
                                        allTotalInterest !== 0 ? Math.round(Math.abs(allTotalInterest / total * 100)) : 0,
                                        allTotalTaxesFees !== 0 ? Math.round(Math.abs(allTotalTaxesFees / total * 100)) : 0,
                                    ],
                                    backgroundColor: [
                                        '#08c177',
                                        '#00aeef',
                                        '#da3551',
                                        '#ff704d',
                                    ],
                                    borderWidth: 0,
                                },
                            ],
                        };
                    }
                }
                return result;
            },
            cashBombValue() {
                return Accounting.formatNumber(this.cashBombSlider.value);
            },
        },
        watch: {
            currentSelectedDate() {
                this.updatedDate();
            },
            chartDataPayoff() {
                this.chartData = this.chartDataPayoff;
            },
            currentDate() {
                this.currentSelectedDate = this.CurrentDate;
            },
        },
        mounted() {
            this.currentSelectedDate = this.CurrentDate;
            this.updatedDate();
            this.getPaymentFrequency();
            this.getCashBombFrequency();
            document.getElementById('monthly').checked = true;
        },
        methods: {
            quicklyBlinkWithChart() {
                this.showChart = false;
                setTimeout(() => {
                    this.showChart = true;
                }, 50);
            },
            updatedDate() {
                const lastDate = new Date(this.currentDate);
                lastDate.setMonth(lastDate.getMonth() + this.loanTermInYears * 12 - 1);
                if (this.currentSelectedDate === null || this.currentSelectedDate.getTime() > lastDate.getTime()) {
                    this.currentDisplayDate = 'Invalid Date';
                } else {
                    const Month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    this.currentDisplayDate = `${Month[this.currentSelectedDate.getMonth()]}, ${this.currentSelectedDate.getDate()} ${this.currentSelectedDate.getFullYear()}`;
                    this.$emit('getCashBombStartDate', this.currentDisplayDate);
                }
            },
            currencyFilter(figure) {
                return Accounting.formatNumber(figure);
            },
            currencyFilterWithPrecision(figure) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision: 2 }) : 0;
            },
            inputOverPayVal(value) {
                this.overpaySlider.value = Accounting.unformat(this.overpaySlider.value, { precision: 0 });
                if (Accounting.unformat(value) <= 500) {
                    this.$emit('getOverPayVal', Accounting.unformat(value));
                }
            },
            formatOverPayValue() {
                this.overpaySlider.value = Accounting.formatMoney(this.overpaySlider.value, { precision: 0 });
            },
            getOverPayVal(value) {
                if (value <= 500) {
                    this.$emit('getOverPayVal', value);
                }
                this.overpaySlider.value = Accounting.formatMoney(this.overpaySlider.value, { precision: 0 });
            },
            inputCashBombVal(value) {
                const inputVal = Accounting.unformat(value);
                if (inputVal <= 100000) {
                    this.$emit('getCashBombVal', inputVal);
                }
            },
            formatCashBombValue() {
                this.cashBombSlider.value = Accounting.formatMoney(this.cashBombSlider.value, { precision: 0 });
            },
            getCashBombVal(value) {
                if (value <= 100000) {
                    this.$emit('getCashBombVal', value);
                    this.cashBombSlider.value = Accounting.formatMoney(this.cashBombSlider.value, { precision: 0 });
                }
            },
            getPaymentFrequency(val) {
                this.$emit('getPaymentFrequency', val);
            },
            getCashBombFrequency() {
                this.$emit('getCashBombFrequency', this.cashBombs);
            },
            getFrequencyInString(value) {
                if (!value) return '';
                return value.replace('ly', '');
            },
        },
    };
</script>

<style lang="scss">
@import "../../../assets/styles/variables";

#lt-yantr {
    .faster-payoff {
        color: #fff;
        background: #17222e;
        width: 100vw;
        position: relative;
        margin-left: -50vw;
        padding: 50px 0;
        left: 50%;

        @media #{$mob-landscape-view} {
            width: auto;
            margin-left: -30px;
            margin-right: -30px;
            padding: 30px 15px;
            left: 0;
        }

        .container-width {
            max-width: 1140px;
            margin: 0 auto;
            padding: 0 25px;

            @media #{$mob-landscape-view} {
                padding: 0;
            }
        }

        h2 {
            color: #fff;
            font-family: "DM Serif Display", "Times New Roman", serif;

            @media #{$mob-landscape-view} {
                font-weight: 400;
                line-height: normal;
                margin-bottom: 15px;
            }
        }

        h5 {
            color: #fff;
        }

        h4 {
            color: #fff;
            font-weight: 300;
            line-height: normal;
            text-align: center;
            max-width: 700px;
            margin: 0 auto 40px;

            @media #{$mob-landscape-view} {
                margin-bottom: 25px;
            }
        }

        .left-block {
            float: left;

            @media #{$mob-landscape-view} {
                float: none;
            }

            .filter-block {
                max-width: 350px;
                list-style: none;
                position: relative;
                margin-bottom: 0;

                @media #{$tab-portrait-view} {
                    max-width: 300px;
                }

                @media #{$mob-landscape-view} {
                    max-width: 100%;
                    margin-bottom: 0;
                }

                &:before {
                    content: "";
                    position: absolute;
                    width: 1px;
                    height: 390px;
                    background: #666;
                    right: -80px;
                    top: 55px;

                    @media screen and (-webkit-min-device-pixel-ratio:0) {
                        height: 406px;
                    }

                    @supports (-ms-ime-align:auto){
                        height: 390px;
                    }

                    @media #{$tab-portrait-view} {
                        right: -33px;
                    }

                    @media #{$mob-landscape-view} {
                        display: none;
                    }
                }

                &>li {
                    text-align: center;
                    background: #2e3843;
                    padding: 15px;
                    margin-top: 25px;
                    position: relative;

                    @media #{$mob-landscape-view} {
                        margin-top: 0;
                    }

                    &:first-child {
                        margin-top: 0;

                        @media #{$mob-portrait-view} {
                            .what-this-link {
                                margin-bottom: 10px;
                            }
                        }

                        &:before {
                            content: "";
                            position: absolute;
                            width: 60px;
                            height: 1px;
                            background: #666;
                            right: -80px;
                            top: 50%;
                        }
                    }

                    &:first-child:before, &:last-child:before, &:nth-child(2):before {
                        content: "";
                        position: absolute;
                        width: 60px;
                        height: 1px;
                        background: #666;
                        right: -80px;
                        top: 50%;

                        @media #{$tab-portrait-view} {
                            width: 30px;
                            right: -40px;
                        }

                        @media #{$mob-landscape-view} {
                            display: none;
                        }
                    }

                    &:first-child:before, &:last-child:before {
                        @media #{$tab-portrait-view} {
                            width: 25px;
                            right: -33px;
                        }
                    }

                    &:nth-child(2):before {
                        width: 280px;
                        right: -300px;

                        @media #{$tab-portrait-view} {
                            width: 290px;
                        }
                    }

                    &:after {
                        content: "";
                        clear: both;
                        display: block;
                    }

                    .btn {
                        @media #{$mob-portrait-view} {
                            padding: .45em 0;
                        }
                    }
                }

                .title {
                    color: #fff;
                    font-size: 17px;
                    font-weight: 600;
                    margin-bottom: 5px;
                    text-transform: uppercase;

                    @media #{$mob-portrait-view} {
                        font-size: 15px;
                        margin: 0;
                    }
                }

                .what-this-link {
                    font-size: 11px;
                    margin-left: 5px;
                    display: none;
                }

                .muted-text {
                    font-size: 13px;
                    color: #ccc;
                    display: inline-block;
                    margin-bottom: 10px;
                }

                .range-slider {
                    margin-bottom: 5px;

                    & + .radio-section {
                        margin-top: 20px;
                    }

                    .vue-slider-component .vue-slider-dot {
                        background: #00aeef;
                        box-shadow: 0 0 0px 7px rgba(0, 174, 239, 0.3);
                    }

                    .slider-val {
                        margin-bottom: 10px;

                        &:after {
                            content: "";
                            clear: both;
                            display: block;
                        }

                        div {
                            display: inline-table;
                            vertical-align: top;
                            margin:0;

                            &:nth-child(1){
                                float: left;
                            }

                            &:nth-child(3){
                                float: right;
                            }

                            &:nth-child(2){
                                max-width: 50%;
                            }

                            &.selected-amt{
                                width: 50%;
                                float: none;

                                &>.slider-input {
                                    color: #00aeef;
                                    font-size: 18px;
                                    line-height: normal;
                                    min-width: 80px;
                                    border: solid 1px rgba(255, 255, 255, 0.1);
                                    padding: 0;
                                    background: transparent;
                                    text-align: center;
                                    height: 30px;
                                    margin-bottom: 0;
                                }
                            }
                        }

                        span {
                            color: #aaa;
                            font-size: 13px;
                            width: 25%;
                            float: left;
                            position: relative;
                            top: 10px;
                        }
                    }
                }

                .radio-section {
                    margin-top: 5px;

                    li {
                        text-align: center;
                        list-style: none;
                        width: 33.33%;
                        position: relative;
                        float: left;

                        &:first-child label {
                            border-radius: 2px 0 0 2px;
                            border-right: none;
                        }

                        &:last-child label {
                            border-radius: 0 2px 2px 0;
                            border-left: none;
                        }
                    }

                    input[type="radio"] {
                        position: absolute;
                        left: -9999px;
                    }

                    input[type="radio"]:checked+label {
                        color: #fff;
                        background: #00aeef;
                    }

                    label {
                        color: #00aeef;
                        font-weight: normal;
                        display: block;
                        padding: 7px 10px;
                        border: solid 1px #00aeef;
                        cursor: pointer;
                        transition: all .3s;
                        @media #{$mob-portrait-view} {
                            padding: 7px 0;
                        }

                        &:after {
                            display: none;
                        }
                    }
                }

                .date-picker-block {
                    text-align: left;
                    margin-top: 10px;

                    @media #{$mob-landscape-view} {
                        display: inline-block;
                        min-width: 256px;
                    }

                    .muted-text {
                        margin-bottom: 0;

                        @media #{$mob-sm-portrait-view} {
                            font-size: 12px;
                        }
                    }
                }
            }

            .extra-pay-btn {
                text-align: center;
                background: #2f3843;
                padding: 10px 15px 25px;
            }
        }

        .right-block {
            float: right;
            text-align: center;

            @media #{$tab-portrait-view} {
                width: 380px;
            }

            @media #{$mob-landscape-view} {
                width: 100%;
                float: none;
            }

            .chart-parent {
                background: #17222e;
                max-width: 430px;
                height: 430px;
                margin: 0 auto 25px;
                padding: 0 20px;
                box-sizing: content-box;

                @media #{$tab-portrait-view} {
                    max-width: 350px;
                    height: 350px;
                }

                @media #{$mob-landscape-view} {
                    height: auto;
                    margin-bottom: 10px;
                }

                canvas {
                    position: absolute;
                    z-index: 1;

                    @media #{$mob-landscape-view} {
                        display: none !important;
                    }
                }
            }

            #chartjs-tooltip-payoff {
                z-index: 2;

                @media #{$mob-landscape-view} {
                    display: none;
                }
            }

            .chart-content {
                position: absolute;
                top: 20%;
                left: 0;
                width: 100%;
                z-index: 0;

                @media #{$tab-portrait-view} {
                    top: 15%;
                }

                @media #{$mob-landscape-view} {
                    position: static;
                    padding-top: 25px;
                }

                label {
                    font-size: 40px;
                    font-weight: bold;
                    margin: 5px 0;
                    display: block;
                    line-height: 1.3em;

                    @media #{$tab-portrait-view} {
                        font-size: 30px;
                    }

                    @media #{$mob-landscape-view} {
                        font-size: 40px;
                        margin: 0;
                    }

                    &.light-gray-text {
                        font-size: 45px;
                        color: #ccc;
                        font-weight: 300;
                        margin: 0;

                        @media #{$mob-landscape-view} {
                            font-size: 30px;
                        }
                    }
                }
                h4 {
                    font-weight: 600;
                    margin: 0;

                    @media #{$mob-landscape-view} {
                        font-size: 18px;
                    }
                }
                small {
                    font-size: 13px;
                }
            }

            .pay-amt {
                margin-bottom: 20px;

                @media #{$mob-landscape-view} {
                    margin-bottom: 0;
                }

                big {
                    font-size: 25px;
                    font-weight: bold;

                    @media #{$mob-landscape-view} {
                        font-size: 14px;
                    }
                }
            }

            .chart-info {

                @media #{$mob-landscape-view} {
                    display: none;
                }

                li {
                    color: #fff;

                    @media #{$tab-portrait-view} {
                        width: 100%;
                        text-align: left;
                        display: block;
                        margin-left: 5px;

                        &:first-child {
                            margin-left: 5px;
                        }
                    }
                }
            }
        }
    }
}
</style>
